import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { FiClock, FiBriefcase, FiCheckCircle, FiUser, FiCalendar, FiMessageSquare, FiAward, FiBookOpen, FiX, FiMapPin, FiShield, FiGlobe, FiMail, FiSend } from 'react-icons/fi';
import { useToast } from '../components/Toast';

const API_URL = `${process.env.REACT_APP_API_URL}/api/lawyers`;
const MSG_API = `${process.env.REACT_APP_API_URL}/api/messages`;

function LawyerProfileModern() {
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [message, setMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lawyer, setLawyer] = useState(null);

  useEffect(() => {
    fetchLawyerDetails();
  }, [id]);

  const fetchLawyerDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/edit.php?id=${id}`);
      if (response.data.success) {
        setLawyer(response.data.data);
        setError(null);
      } else {
        setError('Lawyer not found');
      }
    } catch (err) {
      console.error('Error fetching lawyer details:', err);
      setError('Failed to load lawyer profile');
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = () => { navigate(`/booking?lawyer_id=${id}`); };

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    const userData = localStorage.getItem('user_data');
    if (!userData) {
      toast.warning('Please login to send a message.');
      navigate('/login');
      return;
    }

    const user = JSON.parse(userData);
    if (!user?.id) {
      toast.warning('Please login to send a message.');
      navigate('/login');
      return;
    }

    try {
      setSendingMessage(true);
      const response = await axios.post(`${MSG_API}/?user_id=${user.id}`, {
        receiver_id: parseInt(id),
        message_text: message
      });

      if (response.data.success) {
        toast.success('Message sent successfully!');
        setMessage('');
        setShowMessageModal(false);
      } else {
        toast.error(response.data.message || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  // Parse specialties (comma-separated)
  const getSpecialties = () => {
    if (!lawyer?.specialty) return [];
    return lawyer.specialty.split(',').map(s => s.trim()).filter(Boolean);
  };

  // Parse education text into structured items
  const getEducation = () => {
    if (!lawyer?.education) return [];
    return lawyer.education.split('\n').filter(Boolean).map((line, idx) => ({
      text: line.trim(),
      index: idx
    }));
  };

  // Parse languages
  const getLanguages = () => {
    if (!lawyer?.languages) return [];
    return lawyer.languages.split(',').map(s => s.trim()).filter(Boolean);
  };

  // Get dynamic stats
  const getBookingCount = () => lawyer?.booking_count || lawyer?.cases || 0;
  const getSuccessRate = () => {
    if (lawyer?.success_rate_dynamic !== undefined && lawyer?.success_rate_dynamic !== null) {
      return parseFloat(lawyer.success_rate_dynamic).toFixed(0);
    }
    return lawyer?.successRate || 0;
  };
  const getExperience = () => lawyer?.experience_years || lawyer?.experience || 0;

  const tabs = [
    { key: 'overview', label: 'Overview', icon: <FiUser className="w-4 h-4" /> },
    { key: 'experience', label: 'Qualifications', icon: <FiAward className="w-4 h-4" /> },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50">
        <div className="h-72" style={{ background: 'linear-gradient(135deg, #0f1f35 0%, #1e3a5f 100%)' }} />
        <div className="max-w-6xl mx-auto px-4 -mt-20">
          <div className="animate-pulse bg-white rounded-2xl p-8">
            <div className="flex items-center gap-6">
              <div className="w-28 h-28 rounded-2xl bg-secondary-200" />
              <div className="space-y-3 flex-1"><div className="h-6 bg-secondary-200 rounded w-48" /><div className="h-4 bg-secondary-200 rounded w-32" /></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !lawyer) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-danger-50 flex items-center justify-center mx-auto mb-4">
            <FiX className="w-8 h-8 text-danger-500" />
          </div>
          <p className="text-danger-600 text-lg font-medium mb-4">{error || 'Lawyer not found'}</p>
          <button onClick={() => navigate('/lawyers')} className="btn-primary">Back to Lawyers</button>
        </div>
      </div>
    );
  }

  const specialties = getSpecialties();

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f1f35 0%, #1e3a5f 50%, #172e4d 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-80 h-80 rounded-full" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)' }} />
        </div>
        <div className="max-w-6xl mx-auto px-4 pt-12 pb-32" />
      </div>

      {/* Profile Card */}
      <div className="max-w-6xl mx-auto px-4 -mt-24 relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6 md:p-8 mb-8">
          <div className="flex flex-col md:flex-row items-start gap-6">
            {/* Avatar */}
            <div className="relative">
              <div className="w-28 h-28 rounded-2xl overflow-hidden bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center flex-shrink-0 ring-4 ring-white shadow-lg">
                {lawyer.profile_image ? (
                  <img src={`${process.env.REACT_APP_API_URL}${lawyer.profile_image}`} alt={lawyer.full_name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-5xl">👨‍⚖️</span>
                )}
              </div>
              {lawyer.verified && (
                <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center ring-3 ring-white">
                  <FiCheckCircle className="w-4 h-4 text-white" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                {lawyer.verified && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-teal-600">
                    <FiCheckCircle className="w-3 h-3" /> Verified
                  </span>
                )}
                {lawyer.city && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-600">
                    <FiMapPin className="w-3 h-3" /> {lawyer.city}
                  </span>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-1">{lawyer.full_name}</h1>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {specialties.map((spec, i) => (
                  <span key={i} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-600">
                    {spec}
                  </span>
                ))}
              </div>

              {/* Stats Row */}
              <div className="flex flex-wrap gap-6">
                {[
                  { icon: <FiClock className="w-4 h-4 text-primary-400" />, value: `${getExperience()}+`, label: 'Years Exp.' },
                  { icon: <FiCheckCircle className="w-4 h-4 text-teal-500" />, value: `${getSuccessRate()}%`, label: 'Success' },
                  { icon: <FiBriefcase className="w-4 h-4 text-accent-500" />, value: getBookingCount(), label: 'Cases' },
                ].map((stat, i) => (
                  <div key={i} className="flex items-center gap-2">
                    {stat.icon}
                    <div>
                      <span className="font-bold text-secondary-900">{stat.value}</span>
                      <span className="text-secondary-400 text-xs ml-1">{stat.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 w-full md:w-auto">
              <button onClick={handleBooking} className="btn-primary py-3 px-6 gap-2">
                <FiCalendar className="w-4 h-4" /> Book Consultation
              </button>
              <button onClick={() => setShowMessageModal(true)} className="btn-ghost py-3 px-6 border border-secondary-200 gap-2">
                <FiMessageSquare className="w-4 h-4" /> Send Message
              </button>
            </div>
          </div>
        </motion.div>

        {/* Main Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 pb-16">
          {/* Main Column */}
          <div className="lg:col-span-2">
            {/* Tabs */}
            <div className="bg-white rounded-2xl shadow-card border border-secondary-100 overflow-hidden">
              <div className="flex border-b border-secondary-100">
                {tabs.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex-1 flex items-center justify-center gap-2 py-4 px-4 text-sm font-medium transition-all relative ${
                      activeTab === tab.key
                        ? 'text-primary-600'
                        : 'text-secondary-400 hover:text-secondary-600'
                    }`}
                  >
                    {tab.icon} {tab.label}
                    {activeTab === tab.key && (
                      <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500" />
                    )}
                  </button>
                ))}
              </div>

              <div className="p-6 md:p-8">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
                    {/* Bio */}
                    <section>
                      <h2 className="text-lg font-semibold text-secondary-900 mb-3 flex items-center gap-2">
                        <FiUser className="w-5 h-5 text-primary-400" /> About {lawyer.full_name}
                      </h2>
                      {lawyer.bio ? (
                        <p className="text-secondary-600 leading-relaxed whitespace-pre-line">{lawyer.bio}</p>
                      ) : (
                        <p className="text-secondary-400 italic">No bio provided yet.</p>
                      )}
                    </section>

                    {/* Areas of Expertise */}
                    {specialties.length > 0 && (
                      <section>
                        <h2 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center gap-2">
                          <FiAward className="w-5 h-5 text-primary-400" /> Areas of Expertise
                        </h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {specialties.map((spec, idx) => (
                            <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-secondary-50 border border-secondary-100 hover:border-primary-200 hover:bg-primary-50/50 transition-colors">
                              <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center text-primary-500 flex-shrink-0">
                                <FiBriefcase className="w-4 h-4" />
                              </div>
                              <span className="text-sm font-medium text-secondary-700">{spec}</span>
                            </div>
                          ))}
                        </div>
                      </section>
                    )}

                    {/* Languages */}
                    {getLanguages().length > 0 && (
                      <section>
                        <h2 className="text-lg font-semibold text-secondary-900 mb-3 flex items-center gap-2">
                          <FiGlobe className="w-5 h-5 text-primary-400" /> Languages
                        </h2>
                        <div className="flex flex-wrap gap-2">
                          {getLanguages().map((lang, idx) => (
                            <span key={idx} className="px-3 py-1.5 rounded-full text-sm font-medium bg-teal-50 text-teal-700 border border-teal-100">
                              {lang}
                            </span>
                          ))}
                        </div>
                      </section>
                    )}
                  </motion.div>
                )}

                {/* Experience / Qualifications Tab */}
                {activeTab === 'experience' && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
                    {/* Education */}
                    <section>
                      <h2 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center gap-2">
                        <FiBookOpen className="w-5 h-5 text-primary-400" /> Education & Qualifications
                      </h2>
                      {getEducation().length > 0 ? (
                        <div className="space-y-4 relative before:absolute before:left-4 before:top-2 before:bottom-2 before:w-px before:bg-secondary-200">
                          {getEducation().map((edu) => (
                            <div key={edu.index} className="flex gap-4 pl-4 relative">
                              <div className="w-3 h-3 rounded-full bg-primary-400 border-2 border-white ring-2 ring-primary-100 mt-1.5 flex-shrink-0 relative z-10" />
                              <div className="bg-secondary-50 rounded-xl p-4 flex-1">
                                <p className="font-medium text-secondary-800">{edu.text}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-secondary-400 italic">No education details provided yet.</p>
                      )}
                    </section>

                    {/* Licenses & Certifications */}
                    <section>
                      <h2 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center gap-2">
                        <FiAward className="w-5 h-5 text-primary-400" /> Licenses & Certifications
                      </h2>
                      <div className="space-y-2">
                        {lawyer.license_number && (
                          <div className="flex items-center gap-3 p-3 rounded-xl bg-teal-50/50 border border-teal-100">
                            <FiCheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                            <span className="text-sm text-secondary-700">License #{lawyer.license_number}</span>
                          </div>
                        )}
                        {lawyer.bar_association && (
                          <div className="flex items-center gap-3 p-3 rounded-xl bg-teal-50/50 border border-teal-100">
                            <FiCheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                            <span className="text-sm text-secondary-700">{lawyer.bar_association}</span>
                          </div>
                        )}
                        {!lawyer.license_number && !lawyer.bar_association && (
                          <p className="text-secondary-400 italic">No license information provided yet.</p>
                        )}
                      </div>
                    </section>

                    {/* Experience Summary */}
                    <section>
                      <h2 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center gap-2">
                        <FiBriefcase className="w-5 h-5 text-primary-400" /> Experience Summary
                      </h2>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="p-4 rounded-xl bg-secondary-50 border border-secondary-100 text-center">
                          <p className="text-2xl font-bold text-primary-600">{getExperience()}+</p>
                          <p className="text-xs text-secondary-500 mt-1">Years of Experience</p>
                        </div>
                        <div className="p-4 rounded-xl bg-secondary-50 border border-secondary-100 text-center">
                          <p className="text-2xl font-bold text-teal-600">{getBookingCount()}</p>
                          <p className="text-xs text-secondary-500 mt-1">Cases Handled</p>
                        </div>
                        <div className="p-4 rounded-xl bg-secondary-50 border border-secondary-100 text-center">
                          <p className="text-2xl font-bold text-accent-600">{getSuccessRate()}%</p>
                          <p className="text-xs text-secondary-500 mt-1">Success Rate</p>
                        </div>
                      </div>
                    </section>
                  </motion.div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6">
              <h3 className="font-semibold text-secondary-900 mb-4">Quick Actions</h3>
              <button onClick={handleBooking} className="w-full btn-primary py-3 gap-2 mb-3">
                <FiCalendar className="w-4 h-4" /> Book Now
              </button>
              <button onClick={() => setShowMessageModal(true)} className="w-full btn-ghost py-3 border border-secondary-200 gap-2">
                <FiMessageSquare className="w-4 h-4" /> Message
              </button>
            </div>

            {/* Availability */}
            <div className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6">
              <h3 className="font-semibold text-secondary-900 mb-3">Availability</h3>
              <p className="text-sm text-secondary-600 capitalize">
                {lawyer.availability || 'Full-time'}
              </p>
            </div>

            {/* Contact Info */}
            {(lawyer.city || lawyer.email) && (
              <div className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6">
                <h3 className="font-semibold text-secondary-900 mb-4">Contact Info</h3>
                <div className="space-y-3">
                  {lawyer.city && (
                    <div className="flex items-center gap-3 text-sm">
                      <FiMapPin className="w-4 h-4 text-primary-400 flex-shrink-0" />
                      <span className="text-secondary-600">{lawyer.city}</span>
                    </div>
                  )}
                  {lawyer.email && (
                    <div className="flex items-center gap-3 text-sm">
                      <FiMail className="w-4 h-4 text-primary-400 flex-shrink-0" />
                      <span className="text-secondary-600">{lawyer.email}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Verified Badge */}
            <div className="bg-gradient-to-br from-teal-50 to-teal-100/50 rounded-2xl border border-teal-200/50 p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-teal-500 flex items-center justify-center mx-auto mb-3">
                <FiShield className="w-6 h-6 text-white" />
              </div>
              <h4 className="font-semibold text-secondary-900 mb-1">Verified Professional</h4>
              <p className="text-xs text-secondary-500">All credentials and background verified by RKS Legal Office</p>
            </div>
          </div>
        </div>
      </div>

      {/* Message Modal */}
      <AnimatePresence>
        {showMessageModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowMessageModal(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-6 border-b border-secondary-100">
                <h3 className="font-semibold text-secondary-900">Message {lawyer.full_name}</h3>
                <button onClick={() => setShowMessageModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-secondary-100 transition-colors">
                  <FiX className="w-4 h-4 text-secondary-400" />
                </button>
              </div>
              <div className="p-6">
                <textarea
                  placeholder="Type your message here..."
                  rows="5"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full px-4 py-3 border border-secondary-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none transition-all"
                />
              </div>
              <div className="flex gap-3 p-6 pt-0">
                <button onClick={() => setShowMessageModal(false)} className="flex-1 btn-ghost py-3 border border-secondary-200">Cancel</button>
                <button
                  onClick={handleSendMessage}
                  disabled={sendingMessage || !message.trim()}
                  className="flex-1 btn-primary py-3 gap-2 disabled:opacity-50"
                >
                  {sendingMessage ? 'Sending...' : <><FiSend className="w-4 h-4" /> Send Message</>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default LawyerProfileModern;
